import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Heart, ShieldCheck, Sparkles, Star, ChevronRight, Quote, Users, BadgeCheck } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { COUNTRIES } from "@/lib/constants";

const HERO_BG = "https://images.pexels.com/photos/38523376/pexels-photo-38523376.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";
const STORY_IMG = "https://images.pexels.com/photos/4307823/pexels-photo-4307823.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const COUPLE_IMG = "https://images.pexels.com/photos/12247880/pexels-photo-12247880.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720";

const FEATURED = [
  { name: "Ayesha, 27", role: "Software Engineer · Toronto", img: "https://images.pexels.com/photos/29811345/pexels-photo-29811345.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { name: "Bilal, 31", role: "Surgeon · London", img: "https://images.pexels.com/photos/7580994/pexels-photo-7580994.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { name: "Meera, 26", role: "Architect · New York", img: "https://images.pexels.com/photos/28517487/pexels-photo-28517487.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { name: "Rayan, 29", role: "Entrepreneur · Karachi", img: "https://images.pexels.com/photos/7580937/pexels-photo-7580937.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
];

const TESTIMONIALS = [
  { name: "Aisha & Daniyal", text: "We found each other across continents — she in Manchester, he in Lahore. Right Match made the distance feel small. Now we're married in Dubai.", city: "Manchester · Lahore" },
  { name: "Nadia & Imran", text: "As Bangladeshi Americans, finding someone who shared our culture and modern values felt impossible — until Right Match. The verification gave our parents peace of mind.", city: "New York · Dhaka" },
  { name: "Simran & Arjun", text: "Long-distance matchmaking that actually works. We connected from Toronto and Mumbai, and our VIP matchmaker guided every step till the rishta.", city: "Toronto · Mumbai" },
];

export default function Home() {
  const navigate = useNavigate();
  const [search, setSearch] = useState({ looking_for: "Bride", age_min: 22, age_max: 32, religion: "", country: "", location: "" });

  const submit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (search.looking_for) params.set("looking_for", search.looking_for);
    params.set("age_min", search.age_min);
    params.set("age_max", search.age_max);
    if (search.religion) params.set("religion", search.religion);
    if (search.country) params.set("country", search.country);
    if (search.location) params.set("location", search.location);
    navigate(`/register?${params.toString()}`);
  };

  return (
    <div className="bg-brand-bg min-h-screen">
      <Navbar />

      {/* HERO */}
      <section className="relative" data-testid="home-hero">
        <div className="relative h-[90vh] min-h-[680px] overflow-hidden">
          <img src={HERO_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
          <div className="absolute inset-0 max-w-7xl mx-auto px-6 flex flex-col justify-center">
            <div className="max-w-2xl animate-fade-up">
              <p className="overline text-brand-gold mb-6">South Asian · Global · Trusted Matchmaking</p>
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-white leading-[1.05] tracking-tight">
                Where modern souls<br />
                <span className="italic text-brand-gold">find their forever.</span>
              </h1>
              <p className="text-white/85 mt-6 text-lg max-w-lg leading-relaxed">
                Curated matches across India, Pakistan, Bangladesh, the UK, USA and Canada. Verified profiles. AI-driven compatibility. A matrimonial experience as intentional as the partnership it builds.
              </p>

              <form onSubmit={submit} className="mt-10 bg-white/95 backdrop-blur-xl border border-white/30 rounded-xl p-6 shadow-2xl" data-testid="hero-search-form">
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                  <div>
                    <label className="overline">Looking for</label>
                    <select value={search.looking_for} onChange={(e) => setSearch({ ...search, looking_for: e.target.value })} className="input-elegant mt-1 text-sm" data-testid="hero-looking-for">
                      <option>Bride</option>
                      <option>Groom</option>
                    </select>
                  </div>
                  <div>
                    <label className="overline">Age min</label>
                    <input type="number" value={search.age_min} onChange={(e) => setSearch({ ...search, age_min: e.target.value })} className="input-elegant mt-1 text-sm" min="18" max="80" data-testid="hero-age-min" />
                  </div>
                  <div>
                    <label className="overline">Age max</label>
                    <input type="number" value={search.age_max} onChange={(e) => setSearch({ ...search, age_max: e.target.value })} className="input-elegant mt-1 text-sm" min="18" max="80" data-testid="hero-age-max" />
                  </div>
                  <div>
                    <label className="overline">Religion</label>
                    <select value={search.religion} onChange={(e) => setSearch({ ...search, religion: e.target.value })} className="input-elegant mt-1 text-sm" data-testid="hero-religion">
                      <option value="">Any</option>
                      <option>Hindu</option>
                      <option>Muslim</option>
                      <option>Christian</option>
                      <option>Sikh</option>
                      <option>Jain</option>
                      <option>Buddhist</option>
                      <option>Parsi</option>
                    </select>
                  </div>
                  <div>
                    <label className="overline">Country</label>
                    <select value={search.country} onChange={(e) => setSearch({ ...search, country: e.target.value })} className="input-elegant mt-1 text-sm" data-testid="hero-country">
                      <option value="">Any</option>
                      {COUNTRIES.map((c) => <option key={c.code} value={c.name}>{c.flag} {c.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="overline">City</label>
                    <input value={search.location} onChange={(e) => setSearch({ ...search, location: e.target.value })} placeholder="Any city" className="input-elegant mt-1 text-sm" data-testid="hero-location" />
                  </div>
                </div>
                <div className="flex flex-col md:flex-row gap-3 mt-5">
                  <button type="submit" className="btn-primary flex-1" data-testid="hero-find-match">Find Your Match</button>
                  <Link to="/register" className="btn-gold flex-1 text-center" data-testid="hero-register">Register Free</Link>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y border-brand-line bg-white">
        <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { icon: Users, label: "5M+ Members", sub: "Across India, Pakistan, Bangladesh, UK, USA, Canada" },
            { icon: BadgeCheck, label: "100% Verified", sub: "Govt. ID & manual review" },
            { icon: Sparkles, label: "AI Compatibility", sub: "Powered by Claude Sonnet" },
            { icon: ShieldCheck, label: "Privacy First", sub: "End-to-end encrypted chat" },
          ].map((t, i) => (
            <div key={i}>
              <t.icon className="h-6 w-6 mx-auto text-brand-gold" strokeWidth={1.5} />
              <p className="font-serif text-lg text-brand-ink mt-2">{t.label}</p>
              <p className="text-xs text-brand-inkLight tracking-wide">{t.sub}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <p className="overline">Why Right Match</p>
            <h2 className="font-serif text-4xl sm:text-5xl text-brand-ink mt-3 leading-tight">
              An old-world promise.<br />
              <span className="italic text-brand-gold">A modern method.</span>
            </h2>
            <div className="divider-gold my-6 max-w-xs" />
            <p className="text-brand-inkLight leading-relaxed">
              We blend the warmth of family-led introductions with the precision of AI compatibility scoring — for South Asian singles across India, Pakistan, Bangladesh, the UK, USA and Canada. Less time browsing, more time meeting people who could actually be the one.
            </p>
          </div>
          <div className="md:col-span-7 grid sm:grid-cols-2 gap-5">
            {[
              { icon: Sparkles, title: "AI Compatibility Score", desc: "Claude Sonnet 4.5 evaluates beliefs, lifestyle and goals — not just photos." },
              { icon: ShieldCheck, title: "Verified Profiles", desc: "Government ID + manual review on every member. Trust is non-negotiable." },
              { icon: Heart, title: "Curated Match Queue", desc: "10 considered matches a day — not 10,000 swipes. Quality over noise." },
              { icon: Star, title: "VIP Matchmaker", desc: "Personal matchmaker who introduces you to hand-picked candidates." },
            ].map((f, i) => (
              <div key={i} className="card-elegant p-6">
                <f.icon className="h-6 w-6 text-brand-maroon" strokeWidth={1.5} />
                <h3 className="font-serif text-xl text-brand-ink mt-4">{f.title}</h3>
                <p className="text-sm text-brand-inkLight mt-2 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PROFILES */}
      <section className="bg-white border-y border-brand-line py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="overline">Featured Profiles</p>
              <h2 className="font-serif text-4xl text-brand-ink mt-2">Recently joined members</h2>
            </div>
            <Link to="/register" className="hidden md:flex items-center gap-1 text-sm text-brand-maroon hover:underline">View all <ChevronRight className="h-4 w-4" /></Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {FEATURED.map((p, i) => (
              <div key={i} className="card-elegant overflow-hidden">
                <div className="aspect-[3/4] overflow-hidden bg-brand-muted">
                  <img src={p.img} alt={p.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-4">
                  <h3 className="font-serif text-lg text-brand-ink">{p.name}</h3>
                  <p className="text-xs text-brand-inkLight mt-1">{p.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SUCCESS STORIES */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <p className="overline text-center">Success Stories</p>
        <h2 className="font-serif text-4xl sm:text-5xl text-center text-brand-ink mt-3 leading-tight">
          Real journeys. <span className="italic text-brand-gold">Real forever.</span>
        </h2>

        <div className="grid md:grid-cols-12 gap-8 mt-16 items-center">
          <div className="md:col-span-5 relative">
            <img src={COUPLE_IMG} alt="" className="rounded-lg shadow-xl" />
            <img src={STORY_IMG} alt="" className="absolute -bottom-10 -right-6 w-1/2 rounded-lg shadow-2xl border-4 border-white hidden md:block" />
          </div>
          <div className="md:col-span-7 md:pl-10">
            <Quote className="h-10 w-10 text-brand-gold" strokeWidth={1} />
            <blockquote className="font-serif text-3xl text-brand-ink mt-4 leading-snug italic">
              &ldquo;We met on a Tuesday — she in Birmingham, he in Hyderabad. Married eleven months later. Right Match didn&apos;t just match us — it understood us.&rdquo;
            </blockquote>
            <p className="mt-6 text-sm text-brand-inkLight">— <span className="text-brand-maroon">Priya & Arjun</span>, married Dec 2025 · Birmingham · Hyderabad</p>
            <div className="mt-8 grid grid-cols-3 gap-4">
              {TESTIMONIALS.map((t, i) => (
                <div key={i} className="border-l-2 border-brand-gold pl-4">
                  <p className="text-xs text-brand-inkLight leading-relaxed line-clamp-4">"{t.text}"</p>
                  <p className="text-xs text-brand-maroon mt-2 font-medium">{t.name} · {t.city}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MEMBERSHIP PLANS */}
      <section className="bg-brand-muted py-24" id="plans">
        <div className="max-w-7xl mx-auto px-6">
          <p className="overline text-center">Membership</p>
          <h2 className="font-serif text-4xl sm:text-5xl text-center text-brand-ink mt-3">Choose your journey</h2>
          <p className="text-xs text-brand-inkMute mt-3 tracking-[0.2em] uppercase text-center">Auto-priced in your currency · ₹ · $ · £ · C$ · ₨ · ৳</p>

          <div className="grid md:grid-cols-3 gap-6 mt-14">
            {[
              { id: "free", name: "Free", price: "₹0", sub: "Get started", features: ["Create your profile", "Browse all profiles", "View full profiles (contacts hidden)", "Upgrade to send interests & chat"], cta: "Register Free", primary: false, link: "/register" },
              { id: "premium", name: "Premium", price: "₹999", sub: "/ month", features: ["Unlimited matches", "Unlimited interests", "Chat with matches", "AI compatibility score", "Verified badge"], cta: "Go Premium", primary: true, link: "/plans" },
              { id: "vip", name: "VIP Matchmaking", price: "₹2,499", sub: "/ month", features: ["Everything in Premium", "Priority profile visibility", "Personal matchmaker", "Background verification", "Horoscope match"], cta: "Become VIP", primary: false, gold: true, link: "/plans" },
            ].map((p, i) => (
              <div key={i} className={`relative p-8 rounded-lg bg-white border ${p.primary ? "border-brand-maroon shadow-2xl md:-translate-y-3" : p.gold ? "border-brand-gold shadow-xl" : "border-brand-line shadow-sm"} transition-all`} data-testid={`plan-card-${p.id}`}>
                {p.primary && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-maroon text-white text-[10px] tracking-[0.2em] uppercase px-3 py-1 rounded-full">Most Popular</span>}
                {p.gold && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-gold text-white text-[10px] tracking-[0.2em] uppercase px-3 py-1 rounded-full">Exclusive</span>}
                <p className="overline">{p.name}</p>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="font-serif text-5xl text-brand-ink">{p.price}</span>
                  <span className="text-sm text-brand-inkLight">{p.sub}</span>
                </div>
                <ul className="mt-6 space-y-3">
                  {p.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-brand-ink"><BadgeCheck className="h-4 w-4 text-brand-gold mt-0.5" /> {f}</li>
                  ))}
                </ul>
                <Link to={p.link} className={`block text-center mt-8 ${p.primary ? "btn-primary" : p.gold ? "btn-gold" : "btn-ghost"}`}>{p.cta}</Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 py-24">
        <p className="overline text-center">Frequently Asked</p>
        <h2 className="font-serif text-4xl text-center text-brand-ink mt-3">Questions, answered</h2>
        <Accordion type="single" collapsible className="mt-10 space-y-3" data-testid="home-faq">
          {[
            { q: "How does Right Match verify profiles?", a: "Every profile undergoes a government ID check followed by a manual review. Premium members also undergo a brief video verification call." },
            { q: "Is my information private?", a: "Yes. Contact details are hidden until you accept an interest, and you control who sees your photos. Conversations are end-to-end encrypted." },
            { q: "What's the difference between Premium and VIP?", a: "Premium unlocks unlimited matches and chat. VIP adds a personal matchmaker, priority visibility, and offline background verification." },
            { q: "Can I cancel anytime?", a: "Absolutely. Subscriptions are month-to-month with no lock-in. You can cancel from your dashboard with a single click." },
          ].map((f, i) => (
            <AccordionItem key={i} value={`q-${i}`} className="border border-brand-line rounded-lg px-6 bg-white">
              <AccordionTrigger className="font-serif text-lg text-brand-ink hover:no-underline py-5">{f.q}</AccordionTrigger>
              <AccordionContent className="text-brand-inkLight leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <Footer />
    </div>
  );
}
